// React
import React, { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
// MUI
import { Stack, Divider, Step, StepLabel, Stepper } from "@mui/material";
// Others
import { useSnackbar } from "notistack";
import EmployeeInfo from "../offline-employee-detail/component/EmployeeInfo";
import EmployeeSettlement from "../offline-employee-detail/component/EmployeeSettlement";
import EmployeeProcessorAccInfo from "../offline-employee-detail/component/EmployeeProcessorAccInfo";
import {
  createEmployee,
  getMdr,
  getPreference,
} from "../offline-employee-detail/model";
import sha256 from "crypto-js/sha256";
import dayjs from "dayjs";
import { ProcessorType } from "utils/utils";
import merchantManagement from "routes/merchantManagement";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import { formPreventEnterSubmit } from "utils/utils";
import usePageState from "utils/usePageState";
import usePageWrapper from "utils/usePageWrapper/usePageWrapper";
import { FormBuilder } from "components/FormBuilder/FormBuilder";

const EMPLOYEE_LOCALE_KEY = "page.employees.";

const AddOfflineEmployee = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { pageState } = usePageState({ pagePath: path });
  const { merchantId: stateMerchantId } = pageState;
  const { enqueueSnackbar } = useSnackbar();
  const formProps = useForm({
    mode: "onChange",
  });
  const { handleSubmit, trigger, reset, getValues } = formProps
  const { callApis, PageWrapper, PageOuterContainer, PageStickyHeader, PageBody, PageStickyFooter, scrollToHeader } = usePageWrapper();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [processorAccMapper, setProcessorAccMapper] = useState({});
  const [activeStep, setActiveStep] = useState(0);

  const steps = useMemo(
    () => [
      {
        key: "info",
        label: t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.info.title`, {
          defaultValue: "Info",
        }),
      },
      {
        key: "processor",
        label: t(
          `${EMPLOYEE_LOCALE_KEY}employeeDetail.detail.subtitle.processorAccountInfo`,
          { defaultValue: "Processor Account Information" },
        ),
      },
    ],
    [t],
  );
  const defaultMerchant = useMemo(
    () => ({
      merchantId: stateMerchantId,
    }),
    [stateMerchantId],
  );

  const normalizePaymentGateway = (paymentGateway) => {
    if (paymentGateway === ProcessorType.OCTOPUS) {
      return ProcessorType.OCTOPUS_HKBN;
    }
    return paymentGateway;
  };

  const convertMdrListToTidmidList = (mdrList, dummyMerchantMdrSet, defaultTid = "") => {
    const tidmidMap = new Map();
    const addMdrEntries = (list) => {
      list?.forEach((mdrObject) => {
        if (
          mdrObject.gatewaySwitchingInfoList &&
          Array.isArray(mdrObject.gatewaySwitchingInfoList)
        ) {
          mdrObject.gatewaySwitchingInfoList.forEach((gatewayInfo) => {
            // Only include entries that have a mid (merchant ID)
            if (gatewayInfo.mid && gatewayInfo.mid.trim() !== "") {
              const paymentGateway = normalizePaymentGateway(
                gatewayInfo.paymentGateway,
              );
              const key = `${gatewayInfo.cardType}-${paymentGateway}`;
              tidmidMap.set(key, {
                cardType: gatewayInfo.cardType,
                paymentGateway,
                tid: defaultTid, // Using default TID as it's not in the mdrList structure
                mid: gatewayInfo.mid.trim(),
              });
            }
          });
        }
      });
    };

    addMdrEntries(dummyMerchantMdrSet);
    addMdrEntries(mdrList);

    return Array.from(tidmidMap.values());
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };
  const getMidAndTid = (processor, cardType) => {
    const config = processorAccMapper?.[cardType]?.[processor];
    let foundMid = config?.mid || "";
    let foundTid = config?.tid || "";
    if (!config || (!foundMid && !foundTid)) {
      for (const [availableCardType, processors] of Object.entries(
        processorAccMapper,
      )) {
        if (processors[processor]) {
          const fallbackConfig = processors[processor];

          if (!foundMid && fallbackConfig.mid) {
            foundMid = fallbackConfig.mid;
            console.log(
              `Found mid for ${processor} from ${availableCardType} cardType`,
            );
          }

          if (!foundTid && fallbackConfig.tid) {
            foundTid = fallbackConfig.tid;
            console.log(
              `Found tid for ${processor} from ${availableCardType} cardType`,
            );
          }

          if (foundMid && foundTid) {
            break;
          }
        }
      }
    }

    return {
      mid: foundMid,
      tid: foundTid,
    };
  };

  const onSubmit = async (formValue) => {
    let isCreateSuccess = false;
    let employeeId = null;
    try {
      setIsSubmitting(true);
      // Get mid and tid for each processor and card type
      const { mid, tid } = getMidAndTid(ProcessorType.ALLINPAY, "VISA");
      const { mid: yedpayMid, tid: yedpayTid } = getMidAndTid(
        ProcessorType.YEDPAY,
        "VISA",
      );
      const { mid: payMeMid, tid: payMeTid } = getMidAndTid(
        ProcessorType.HSBC,
        "PAYME",
      );
      const { mid: octopusHkbnMid } = getMidAndTid(
        ProcessorType.OCTOPUS_HKBN,
        "OCTOPUS",
      );
      const { mid: octopusBbmslMid } = getMidAndTid(
        ProcessorType.OCTOPUS_BBMSL,
        "OCTOPUS",
      );
      const { mid: amexMid, tid: amexTid } = getMidAndTid(
        ProcessorType.AMEX,
        "AMEX",
      );
      const { cupMid, cupTid } = { cupMid: "", cupTid: "" };
      const payload = {
        ...formValue,
        firstName: formValue.firstname,
        lastName: formValue.lastname,
        password: sha256(formValue.password).toString(),
        componentAllowed: formValue.permission,
        isAutosettlement: formValue.isAutosettlement,
        settlementDate: formValue.settlementDate
          ? dayjs(formValue.settlementDate).toISOString()
          : null,
        mid,
        tid,
        yedpayMid,
        yedpayTid,
        payMeMid,
        payMeTid,
        octopusHkbnMid,
        octopusBbmslMid,
        amexMid,
        amexTid,
        cupMid,
        cupTid,
      };
      // Remove uesless payload
      delete payload.confirmPassword;
      delete payload.permission;
      delete payload.firstname;
      delete payload.lastname;
      const [createEmployeeResp] = await callApis([{
        api: createEmployee,
        payload: payload,
        blockOnError: false,
        blockOnUnauthorized: false,
      }])
      console.log("Create employee response", createEmployeeResp);
      if (createEmployeeResp.isSuccess) {
        snackbar(
          t(
            EMPLOYEE_LOCALE_KEY +
            "employeeDetail.fetchMessage.createEmployeeSuccess",
          ),
          "success",
        );
        reset();
        isCreateSuccess = true;
        employeeId = createEmployeeResp.data.employeeId;
      } else {
        createEmployeeResp.isError && snackbar(
          t(
            EMPLOYEE_LOCALE_KEY +
            "employeeDetail.fetchMessage.createEmployeeFailed",
          ),
          "error",
        );
      }
    } catch (error) {
      console.error("Create Employee error:", error);
      snackbar(
        `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.createEmployeeFailed")}: ${error.message}`,
        "error",
      );
    } finally {
      if (isCreateSuccess) {
        setTimeout(() => {
          setIsSubmitting(false);
          navigateToEmployeeDetailPage(employeeId);
          deleteCurrentTab();
        }, 1000);
      } else {
        setIsSubmitting(false);
      }
    }
  };

  const navigateToEmployeeDetailPage = (employeeId) => {
    const path = "/merchant-management/employees/offline-employee-detail";
    const payload = {
      name: merchantManagement
        .find((tab) => tab.tabName === "Employees")
        .page.find((page) => page.path === path).name + employeeId,
      path: path,
      state: {
        employeeId: employeeId,
      }
    };
    redirectFn("redirectPage", payload);
  };

  const deleteCurrentTab = () => {
    const basePath = "/merchant-management/employees/add-offline-employee";
    const payload = {
      tabId: path, // exact current tab id (may include state suffix)
      name: merchantManagement
        .find((tab) => tab.tabName === "Employees")
        .page.find((page) => page.path === basePath).name,
      path: basePath,
    };
    redirectFn("close", payload);
  };

  const fetchProcessorAccountInfo = async () => {
    console.log("handleOnClickNext");
    setIsLoading(true);
    const formValue = getValues();
    console.log("Create Employee formValue", formValue);
    try {
      const [mdrResp, preferenceResp] = await callApis([
        {
          api: getMdr,
          payload: formValue.merchantId,
          blockOnError: false,
          blockOnUnauthorized: false,
        },
        {
          api: getPreference,
          payload: { merchantId: formValue.merchantId },
          blockOnError: false,
          blockOnUnauthorized: false,
        }
      ])
      if (!mdrResp.isSuccess || !preferenceResp.isSuccess) {
        mdrResp.isError && snackbar(
          t(EMPLOYEE_LOCALE_KEY + "createEmployee.toast.getMdr.failed"),
          "error",
        );
        preferenceResp.isError && snackbar(
          t(EMPLOYEE_LOCALE_KEY + "createEmployee.toast.getPreference.failed"),
          "error",
        );
        return false;
      };
      const mdrList = mdrResp?.data?.mdrList;
      const dummyMerchantMdrSet = mdrResp?.data?.dummyMerchantMdrSet;
      const preference = preferenceResp?.data.preference;
      const allinpayMid = mdrList
        .flatMap((mdr) => mdr.gatewaySwitchingInfoList)
        .find(
          (gateway) =>
            gateway.paymentGateway === "ALLINPAY" &&
            gateway.mid &&
            gateway.mid !== "",
        )?.mid;
      if (!allinpayMid) {
        snackbar(
          t(EMPLOYEE_LOCALE_KEY + "createEmployee.allinpayMidNotFound"),
          "error",
        );
        return false;
      }
      const tidmidList = convertMdrListToTidmidList(
        mdrList,
        dummyMerchantMdrSet,
      );
      const employee = {
        tidmidList,
        preference,
      };
      setEmployee(employee);
      return true;
    } catch (error) {
      console.error("Error fetching mdr and preference:", error);
      snackbar(
        `${t(EMPLOYEE_LOCALE_KEY + "employeeDetail.fetchMessage.fetchEmployeeFail")}: ${error.message}`,
        "error",
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (isSubmitting) return;

    const isValid = await trigger(undefined, { shouldFocus: true });
    if (!isValid) return false;

    if (activeStep === 0) {
      const isProcessorAccountReady = await fetchProcessorAccountInfo();
      if (!isProcessorAccountReady) return;
    }

    setActiveStep((step) => Math.min(step + 1, steps.length - 1));
    scrollToHeader();
  };

  const handleBack = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (isSubmitting) return;

    setActiveStep((step) => Math.max(step - 1, 0));
    scrollToHeader();
  };

  return (
    <PageOuterContainer>
      <PageWrapper noPadding isLoading={isSubmitting}>
        <PageStickyHeader noSticky>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              "& .MuiStepIcon-root.Mui-active": { color: "rgb(54, 180, 191)" },
              "& .MuiStepIcon-root.Mui-completed": { color: "rgb(54, 180, 191)" },
            }}
          >
            {steps.map((step) => (
              <Step key={step.key}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </PageStickyHeader>
        <form
          noValidate
          onKeyDown={formPreventEnterSubmit}
        >
          <PageBody>
            <FormBuilder.Section>
              {activeStep === 0 && (
                <>
                  <EmployeeInfo
                    formProps={formProps}
                    isEdit={true}
                    isLoading={isLoading}
                    redirectFn={redirectFn}
                    isCreate={true}
                    defaultMerchant={defaultMerchant}
                    sectionTitle={t(`${EMPLOYEE_LOCALE_KEY}employeeDetail.detail.subtitle.basicInformation`)}
                    sectionTitleVariant="h3"
                  />
                  <Divider />
                  <EmployeeSettlement
                    callApis={callApis}
                    formProps={formProps}
                    isEdit={true}
                    isLoading={isLoading}
                    employee={null}
                    isOtherProcessor={false}
                    isCreate={true}
                  />
                </>
              )}
              {activeStep === 1 && employee && (
                <>
                  <EmployeeProcessorAccInfo
                    employee={employee}
                    isLoading={isLoading}
                    isDisabled={isSubmitting}
                    isCreate={true}
                    processorAccMapper={processorAccMapper}
                    setProcessorAccMapper={setProcessorAccMapper}
                  />
                </>
              )}
            </FormBuilder.Section>
            <PageStickyFooter>
              <Stack
                direction="row"
                justifyContent="space-between"
                gap={2}
              >
                <ButtonMAS
                  variant="bordered"
                  disabled={activeStep === 0 || isLoading || isSubmitting}
                  sx={{ visibility: activeStep === 0 ? "hidden" : "visible" }}
                  onClick={handleBack}
                  type="button"
                >
                  {t("general.button.previous", { defaultValue: "Previous" })}
                </ButtonMAS>
                {activeStep < steps.length - 1 ? (
                  <ButtonMAS
                    variant="primary"
                    isLoading={activeStep === 0 && isLoading}
                    onClick={handleNext}
                    type="button"
                    disabled={isLoading || isSubmitting}
                  >
                    {t("general.button.next", { defaultValue: "Next" })}
                  </ButtonMAS>
                ) : (
                  <ButtonMAS
                    type="button"
                    variant="primary"
                    isLoading={isSubmitting}
                    onClick={handleSubmit(onSubmit)}
                  >
                    {t("general.button.submit", { defaultValue: "Submit" })}
                  </ButtonMAS>
                )}
              </Stack>
            </PageStickyFooter>
          </PageBody>
        </form>
      </PageWrapper>
    </PageOuterContainer>
  );
};

export default React.memo(AddOfflineEmployee);
